import { TextField, type TextFieldProps } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { type ForwardedRef, forwardRef } from "react";

export type TextInputProps = TextFieldProps;

/**
 * MUI TextField wrapper that applies Berlin design system defaults
 * (border, focus ring, hover, placeholder, and helper text styling).
 * All props and styles can be overridden by consumers via `sx`.
 */
function BaseTextInput(props: TextInputProps, ref: ForwardedRef<HTMLInputElement>) {
  const { sx, FormHelperTextProps, inputRef, ...rest } = props;

  return (
    <TextField
      {...rest}
      variant="outlined"
      inputRef={inputRef ?? ref}
      FormHelperTextProps={{
        ...FormHelperTextProps,
        sx: mergeSxProps(
          (theme) => ({
            color: theme.palette.base?.inactive,
            marginLeft: 0,
          }),
          FormHelperTextProps?.sx
        ),
      }}
      sx={mergeSxProps(
        (theme) => ({
          "& .MuiOutlinedInput-root": {
            borderRadius: theme.borderRadius?.large,
            transition: "box-shadow 0.15s ease-in-out",
            "& fieldset": {
              borderColor: theme.palette.border?.base,
            },
            "&:hover fieldset": {
              borderColor: theme.palette.border?.strong,
            },
            "&.Mui-focused": {
              boxShadow: `0px 0px 0px 4px ${theme.palette.primary.main}14`,
            },
            "&.Mui-focused fieldset": {
              borderColor: theme.palette.primary.main,
              borderWidth: theme.borderWidth?.regular,
            },
          },
          "& .MuiOutlinedInput-input": {
            "&::placeholder": {
              color: theme.palette.text.disabled,
              opacity: 1,
            },
          },
        }),
        sx
      )}
    />
  );
}

export const TextInput = forwardRef(BaseTextInput);
TextInput.displayName = "TextInput";
